import { FormSectionLayout } from "@homebound/beam";
import {
  BaseChangeEventFormInput,
  BaseChangeEventFormState,
} from "src/routes/projects/change-events/guided/baseChangeEventForm";
import { ChangeEventWorkflowComments } from "src/routes/projects/change-events/guided/components/ChangeEventWorkflowComments";
import {
  changeEventQueryFor,
  GuidedChangeEventStep,
  isBoolPreContract,
} from "src/routes/projects/change-events/guided/utils";
import { ReviewConfigurationStep } from "src/routes/projects/change-events/guided/workflows/applyConfiguration/ReviewConfigurationStep";
import {
  ReconcileImpactedCommitmentsFormInput,
  ReconcileImpactedCommitmentsStep,
} from "src/routes/projects/change-events/guided/workflows/sharedSteps/ReconcileImpactedCommitmentsStep";
import { ReviewStep } from "src/routes/projects/change-events/guided/workflows/sharedSteps/ReviewStep";
import { WorkflowStep } from "src/routes/projects/change-events/guided/workflows/types";

/**
 * Placeholder shown for steps that aren't built yet. Still renders the step's own
 * `FormSectionLayout` so the empty step keeps the same centered column as a real one.
 */
export function NotInPrototypeYet(props: { title: string }) {
  const { title } = props;
  return <FormSectionLayout title={title} description="This step is not part of the prototype yet." sections={[]} />;
}

export function reviewConfigurationStep<F extends BaseChangeEventFormInput>(): WorkflowStep<F> {
  return {
    value: GuidedChangeEventStep.ReviewConfiguration,
    label: "Review Configuration",
    // Read-only steps are always complete
    isComplete: () => true,
    // Disabled until the change event is saved (or dry ran)
    disabled: (form) => !changeEventQueryFor(form as BaseChangeEventFormState<F>).changeEventId,
    // Hidden for BOOL projects until the contract is signed, since their checkout configuration isn't meaningful before then
    hidden: (_, project) => isBoolPreContract(project),
    render: (form) => <ReviewConfigurationStep formState={form as BaseChangeEventFormState<F>} />,
  };
}

export function reviewImpactStep<F>(): WorkflowStep<F> {
  return {
    value: GuidedChangeEventStep.ReviewImpact,
    label: "Review Impact",
    isComplete: () => false,
    disabled: () => true,
    render: () => <NotInPrototypeYet title="Review Impact" />,
  };
}

export function reviewStep<F extends BaseChangeEventFormInput>(): WorkflowStep<F> {
  return {
    value: GuidedChangeEventStep.Review,
    label: "Review",
    isComplete: (form) => form.valid,
    render: (form) => <ReviewStep formState={form as BaseChangeEventFormState<F>} />,
    rightPaneTriggers: (form) => {
      const changeEventId = (form as BaseChangeEventFormState<F>).id.value;
      return changeEventId
        ? [
            {
              icon: "comment",
              label: "Comments",
              content: <ChangeEventWorkflowComments changeEventId={changeEventId} />,
            },
          ]
        : [];
    },
  };
}

export function reconcileImpactedCommitmentsStep<
  F extends BaseChangeEventFormInput & ReconcileImpactedCommitmentsFormInput,
>(): WorkflowStep<F> {
  return {
    value: GuidedChangeEventStep.Reconcile,
    label: "Reconcile",
    // Only shown when the change touches a PO, which the save before Review Impact works out
    hidden: (form) => !form.hasImpactedCommitments.value,
    isComplete: (form) => form.valid,
    render: (form) => <ReconcileImpactedCommitmentsStep formState={form as BaseChangeEventFormState<F>} />,
  };
}
